import type { UserManagerSettings } from "oidc-client-ts";
import { WebStorageStateStore } from "oidc-client-ts";
import { config } from "../config";

/**
 * The scope set, verbatim from the path already proven against this ZITADEL.
 *
 * 🪤 `urn:ankhnet:` is NOT a scope namespace. An earlier attempt find-and-
 * replaced "zitadel" -> "ankhnet" here. An OAuth server DROPS scopes it does
 * not recognise rather than failing the request, so the login worked, a token
 * came back, it looked perfect — and it carried NO namespace, NO roles and NO
 * backend audience. Do not edit these strings casually.
 *
 * 🪤 Note the PLURAL `projects` in the roles scope. The scope NAME and the
 * claim KEY are different things and do not match each other.
 */
export function scopes(projectId: string): string {
  return [
    "openid",
    "profile",
    "email",
    "urn:zitadel:iam:user:metadata",
    // 🪤🪤 BOTH forms, deliberately, and the difference is not cosmetic.
    //
    // The SINGULAR `project` is the form a BROWSER login needs; the PLURAL
    // `projects` is the form proven on the SERVICE path. This file originally
    // carried only the plural, because the scope set was copied verbatim from
    // the working machine client -- and a machine and a human do not ask for
    // roles the same way. The result was a token that authenticated perfectly,
    // carried its namespace metadata, and had NO roles at all, so every report
    // would 403 with nothing in the token to explain why.
    //
    // Asking for both is strictly safer than choosing: an OAuth server DROPS a
    // scope it does not recognise rather than failing the request, so the form
    // that does not apply costs nothing, and resolveRoles() already unions the
    // two claim KEYS these produce.
    //
    // 🔑 Scope NAME and claim KEY are different things and do not match each
    // other. Do not "tidy" one to look like the other.
    "urn:zitadel:iam:org:project:roles",
    "urn:zitadel:iam:org:projects:roles",
    // Puts the PROJECT id in `aud`, which is what the Go backends require.
    `urn:zitadel:iam:org:project:id:${projectId}:aud`,
    // 🔑 offline_access -> a refresh token, and the reason is the embedded
    // sign-in (routes/SignIn.tsx). A login completed by radaccess through
    // ZITADEL's Session API sets NO cookie on sso.ankhnet.io, so the silent
    // renew oidc-client-ts would otherwise do -- a hidden iframe with
    // prompt=none -- has no session to find and would land the iframe on our
    // own /auth/login. With a refresh token the library renews by a POST to
    // the token endpoint instead and never opens the iframe. The app's
    // ZITADEL registration must allow the refresh-token grant (the setup
    // script adds it); a server that does not simply drops this scope.
    "offline_access",
  ].join(" ");
}

export function oidcSettings(): UserManagerSettings {
  const { oidc } = config();
  return {
    authority: oidc.authority,
    client_id: oidc.clientId,
    redirect_uri: `${window.location.origin}/callback`,
    post_logout_redirect_uri: window.location.origin,
    response_type: "code",
    scope: scopes(oidc.projectId),
    // Authorization Code + PKCE. This client is registered in ZITADEL as
    // USER_AGENT with auth method NONE, so PKCE is the whole proof.
    loadUserInfo: false,
    automaticSilentRenew: true,
    // sessionStorage, not localStorage: a token should not outlive the tab.
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
  };
}

/**
 * Strip ?code=&state= after the exchange so a refresh does not replay it.
 * This is an AuthProvider prop, NOT a UserManagerSettings key — putting it in
 * the settings object type-checks in JS and is silently ignored.
 */
export function onSigninCallback(): void {
  window.history.replaceState({}, document.title, window.location.pathname);
}
